import { useEffect, useState } from "react";
import { useLanguage } from "../i18n";

type Consent = { version: 1; analytics: boolean; marketing: boolean };
const STORAGE_KEY = "axon_consent_v1";

function applyConsent(consent: Consent) {
  document.documentElement.dataset.consentAnalytics = consent.analytics ? "granted" : "denied";
  document.documentElement.dataset.consentMarketing = consent.marketing ? "granted" : "denied";
  window.dispatchEvent(new CustomEvent("axon:consent", { detail: consent }));
}

export function CookieConsent() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return setOpen(true);
      const consent = JSON.parse(stored) as Consent;
      if (consent.version !== 1) return setOpen(true);
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
      applyConsent(consent);
    } catch {
      setOpen(true);
    }
  }, []);

  function save(next: Omit<Consent, "version">) {
    const consent: Consent = { version: 1, ...next };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    applyConsent(consent);
    setAnalytics(next.analytics);
    setMarketing(next.marketing);
    setOpen(false);
    setPreferences(false);
  }

  return (
    <>
      <button className="cookie-trigger" type="button" onClick={() => setOpen(true)}>{t.cookie.trigger}</button>
      {open && (
        <div className="cookie-layer" role="presentation">
          <section className="cookie-panel" role="dialog" aria-modal="true" aria-labelledby="cookie-title">
            <div>
              <p className="eyebrow">{t.cookie.eyebrow}</p>
              <h2 id="cookie-title">{t.cookie.title}</h2>
              <p>{t.cookie.body} <a className="cookie-privacy-link" href="/datenschutz">{t.footer.privacy}</a></p>
            </div>
            {preferences && (
              <div className="cookie-options">
                <label><span><strong>{t.cookie.necessary}</strong><small>{t.cookie.necessaryDescription}</small></span><input type="checkbox" checked disabled /></label>
                <label><span><strong>{t.cookie.analytics}</strong><small>{t.cookie.analyticsDescription}</small></span><input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} /></label>
                <label><span><strong>{t.cookie.marketing}</strong><small>{t.cookie.marketingDescription}</small></span><input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} /></label>
              </div>
            )}
            <div className="cookie-actions">
              <button type="button" className="text-button" onClick={() => save({ analytics: false, marketing: false })}>{t.cookie.necessaryOnly}</button>
              {preferences ? (
                <button type="button" className="button button--dark" onClick={() => save({ analytics, marketing })}>{t.cookie.save}</button>
              ) : (
                <button type="button" className="text-button" onClick={() => setPreferences(true)}>{t.cookie.preferences}</button>
              )}
              <button type="button" className="button button--pink" onClick={() => save({ analytics: true, marketing: true })}>{t.cookie.acceptAll}</button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
